import { createRoute } from "@tanstack/react-router";
import { appAuthRoute } from "./index";
import { ENTITY } from "../../constants";
import { checkPermissionsAndRedirect } from "../../helpers/checkPermissions";
import { adGroupTemplateViewLoader, adGroupTemplateFormLoader } from "../loaders/adGroupTemplate";
import { AdGroupTemplateBreadcrumb } from "../../breadcrumbs/adGroupTemplate";
import Spinner from "../../components/general/Spinner";
// import loading pages
import EntityListLoading from "../../pages/entities/loading/EntityListLoading";
import EntityCreateLoading from "../../pages/entities/loading/EntityCreateLoading";
import EntityViewLoading from "../../pages/entities/loading/EntityViewLoading";
import EntityEditLoading from "../../pages/entities/loading/EntityEditLoading";

const { ADGROUPTEMPLATE } = ENTITY;

export const adGroupTemplateRouteFactory = (parent = appAuthRoute) => {
  // If parent route is appAuthRoute => the route is NOT nested (ie. its directly under appAuthRoute)
  // If parent route is NOT appAuthRoute => route is nested
  const isNestedRoute = parent !== appAuthRoute;

  // Root

  const adGroupTemplateRoute = createRoute({
    getParentRoute: () => parent,
    path: ADGROUPTEMPLATE.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: ADGROUPTEMPLATE.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: ADGROUPTEMPLATE.label.upFirstPlural,
    },
  });

  // List adGroupTemplates

  const adGroupTemplateListRoute = createRoute({
    getParentRoute: () => adGroupTemplateRoute,
    path: "/",
    pendingComponent: () => <EntityListLoading entity={ADGROUPTEMPLATE} />,
  }).lazy(() => import("./adGroupTemplate.lazy").then((d) => d.AdGroupTemplateListRoute));

  // Create adGroupTemplate

  const adGroupTemplateCreateRoute = createRoute({
    getParentRoute: () => adGroupTemplateRoute,
    path: "create",
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: ADGROUPTEMPLATE.type.singular, action: "CREATE" },
      ]),
    staticData: {
      breadcrumb: "Create new",
    },
    loader: adGroupTemplateFormLoader,
    pendingComponent: () => <EntityCreateLoading entity={ADGROUPTEMPLATE} />,
  }).lazy(() => import("./adGroupTemplate.lazy").then((d) => d.AdGroupTemplateCreateRoute));

  // View adGroupTemplate

  const adGroupTemplateViewRootRoute = createRoute({
    getParentRoute: () => adGroupTemplateRoute,
    path: "$adGroupTemplateId",
    staticData: {
      breadcrumb: AdGroupTemplateBreadcrumb,
    },
    loader: adGroupTemplateViewLoader,
  });

  // Edit adGroupTemplate

  const adGroupTemplateEditRoute = createRoute({
    getParentRoute: () => adGroupTemplateViewRootRoute,
    path: "edit",
    staticData: {
      breadcrumb: "Edit",
    },
    loader: adGroupTemplateFormLoader,
    pendingComponent: () => <EntityEditLoading entity={ADGROUPTEMPLATE} />,
  }).lazy(() => import("./adGroupTemplate.lazy").then((d) => d.AdGroupTemplateEditRoute));

  // View adGroupTemplate Layout

  const adGroupTemplateViewLayoutRoute = createRoute({
    getParentRoute: () => adGroupTemplateViewRootRoute,
    id: "adGroupTemplate-view-layout",
    pendingComponent: () => <EntityViewLoading entity={ADGROUPTEMPLATE} />,
  }).lazy(() =>
    import("./adGroupTemplate.lazy").then((d) => d.AdGroupTemplateViewLayoutRoute)
  );

  // View adGroupTemplate - General Tab

  const adGroupTemplateViewLayoutGeneralTabRoute = createRoute({
    getParentRoute: () => adGroupTemplateViewLayoutRoute,
    path: "/",
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adGroupTemplate.lazy").then((d) => d.AdGroupTemplateViewLayoutGeneralTabRoute)
  );

  // View adGroupTemplate - About Tab

  const adGroupTemplateViewLayoutAboutTabRoute = createRoute({
    getParentRoute: () => adGroupTemplateViewLayoutRoute,
    path: "about",
    staticData: {
      breadcrumb: "About",
    },
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adGroupTemplate.lazy").then((d) => d.AdGroupTemplateViewLayoutAboutTabRoute)
  );

  // Route tree
  adGroupTemplateRoute.addChildren([
    // if the route is nested within a parent route, we do not provide ListRoute
    // because the route is already nested in a TabListRoute
    ...(isNestedRoute ? [] : [adGroupTemplateListRoute]),
    adGroupTemplateCreateRoute,
    adGroupTemplateViewRootRoute.addChildren([
      adGroupTemplateEditRoute,
      adGroupTemplateViewLayoutRoute.addChildren([
        adGroupTemplateViewLayoutGeneralTabRoute,
        adGroupTemplateViewLayoutAboutTabRoute,
      ]),
    ]),
  ]);

  return adGroupTemplateRoute;
};

const adGroupTemplateRoute = adGroupTemplateRouteFactory();

export default adGroupTemplateRoute;
